"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Specialisation 5017 — KUK programme-specialisation cards.
 *
 * Port of kuk-campus-portal `components/Degree/Specialisations.tsx`: the row of
 * cards on `/online-ma-lp` offering the specialisations of a parent programme —
 * thumbnail, programme abbreviation, specialisation name, blurb, duration, and a
 * "Know more" / "Brochure" pair.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * TWO LAYOUTS, ONE LOOP. v1 writes the card out twice — a compact
 * thumbnail-beside-text row below `md`, and a tall image-on-top card above it —
 * duplicating every field and both CTAs. They differ only in arrangement, so
 * this renders one card that changes shape at `tab`, which is half the markup
 * and cannot drift between breakpoints.
 *
 * BOTH CTAs OPEN THE LEAD FORM. In v1 "Know more" routes to the specialisation's
 * own page on a normal programme page, but on a landing page
 * (`pathname.includes("lp")`) it opens the lead modal instead — and every page
 * this variant serves IS a landing page. Rather than sniff the URL, both CTAs
 * open the lead drop here, which is what the reference actually does in this
 * context. A `cta_link` on a row still wins if an editor sets one.
 *
 * DROPPED — v1's horizontal `flex gap-5` row, which overflows off-screen once
 * there are more than about four specialisations and gives no way to reach them.
 * The cards wrap here instead.
 */

/** v1: `h-48` on the card image, and a 16px glyph beside the compact title. */
const CARD_IMAGE_HEIGHT = 192;
const COMPACT_ICON_SIZE = "16";

interface SpecialisationRow {
  id?: string | number;
  /** Card thumbnail. */
  meta_image?: string;
  meta_alt_image?: string;
  /** The specialisation, e.g. "English". */
  meta_title?: string;
  meta_description?: string;
  /** e.g. "2 years". Rendered only when set, as in the reference. */
  meta_sub_title?: string;
  /** Optional override — without it the CTA opens the lead form. */
  meta_link?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface Specialisation5017Data {
  status?: boolean | number | string;
  /**
   * Heading. Uses the `{phrase}` convention shared with the other KUK widgets:
   * the braced run is tinted, so "{Online MA} Specialisations" renders the
   * programme abbreviation in the accent colour, as the reference does with its
   * own `<span className="text-primary-900">`.
   */
  title?: string;
  title_text_color?: string;
  /** Tint for the braced run in `title`, and for the card's specialisation name. */
  cta_text_color?: string;
  /** Band fill, painted under the background image. */
  background_color?: string;
  background_image?: string;
  alt_background_image?: string;
  /** Primary CTA (the reference's "Brochure"). */
  cta_text?: string;
  cta_background_color?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
  /** Secondary CTA (the reference's "Know more"). */
  secondary_cta_text?: string;
  secondary_cta_text_color?: string;
  metadata?: SpecialisationRow[];
}

/**
 * A nullable CMS column, not a string: these come straight from MySQL, where an
 * unset URL is NULL — and a destructuring default only fills in `undefined`, so
 * `null` reaches here intact.
 */
function normalizeHref(href: string | null | undefined): string {
  const h = (href ?? "").trim();
  if (!h) return "";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** Splits "{Online MA} Specialisations" into tinted and plain runs. */
function renderTitle(title: string, accent: string) {
  return title.split(/(\{[^}]*\})/g).map((part, i) =>
    part.startsWith("{") && part.endsWith("}") ? (
      <span key={i} style={{ color: accent }}>
        {part.slice(1, -1)}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

/**
 * v1's `.custom-underline` — the accent rule under the heading. This variant's
 * `<Typography className="mb-10" asChild>` takes Typography's DEFAULT variant,
 * which carries `custom-underline`, so the reference draws one here; it was
 * missing from this port.
 *
 * `specialisation` has no spare colour column, and v1 paints the rule and the
 * `{...}` highlight the same brand colour, so both read `cta_text_color`.
 */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

const Specialisation5017 = (props: { data?: Specialisation5017Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | Specialisation5017Data
    | undefined;
  const dispatch = useDispatch();

  const {
    title = "",
    title_text_color = "#101112",
    cta_text_color = "#9F1C33",
    background_color = "#F5F5F5",
    background_image = "",
    alt_background_image = "",
    cta_text = "Brochure",
    cta_background_color = "#9F1C33",
    cta_icon = "",
    cta_icon_alt = "",
    secondary_cta_text = "Know more",
    secondary_cta_text_color = "#031F44",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const items = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_image))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const openLeadForm = useCallback(
    (label: string, index: number) => {
      ctaClick({
        section: "specialisation_5017",
        component: "specialisation_card",
        label,
        step: "1",
        entity: "specialisation_5017",
        parentEntity: "specialisation_5017",
      });
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
    },
    [dispatch],
  );

  if (!status || items.length === 0) return null;

  return (
    <section
      className="flex w-full items-center justify-center bg-cover bg-center bg-no-repeat py-10"
      style={{
        backgroundColor: background_color,
        ...(background_image
          ? {
              backgroundImage: `url("${background_image.replace(/"/g, "%22")}")`,
            }
          : {}),
      }}
      aria-label={alt_background_image || undefined}
    >
      <div className="w-full max-w-[1120px] p-5 tab:px-20">
        {title ? (
          <h2
            className="relative mb-10 w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_text_color }}
          >
            {renderTitle(title, cta_text_color)}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: cta_text_color }}
            />
          </h2>
        ) : null}

        <div className="flex flex-wrap gap-5">
          {items.map((item, index) => {
            const href = normalizeHref(item.meta_link);
            const primary = (
              <button
                type="button"
                onClick={() => openLeadForm(cta_text, index)}
                className="inline-flex h-10 w-1/2 items-center justify-center gap-2 rounded-md text-sm font-medium text-white shadow transition-colors"
                style={{ backgroundColor: cta_background_color }}
              >
                {cta_icon ? (
                  <DynamicIcon
                    iconName={cta_icon}
                    width="14"
                    height="14"
                    color="#FFFFFF"
                  />
                ) : null}
                {cta_text}
              </button>
            );
            const secondary = (
              <button
                type="button"
                onClick={() =>
                  href
                    ? window.location.assign(href)
                    : openLeadForm(secondary_cta_text, index)
                }
                className="inline-flex h-10 w-1/2 items-center justify-center rounded-md border text-sm font-semibold transition-colors"
                style={{
                  color: secondary_cta_text_color,
                  borderColor: secondary_cta_text_color,
                }}
              >
                {secondary_cta_text}
              </button>
            );

            return (
              <div
                key={item.id ?? index}
                className={cn(
                  "flex w-full overflow-hidden rounded-3xl bg-white shadow-md",
                  // Below `tab` the thumbnail sits beside the copy; above it the
                  // card becomes a column with the image on top.
                  "flex-row items-stretch",
                  "tab:w-auto tab:min-w-56 tab:max-w-80 tab:flex-1 tab:flex-col tab:items-start tab:justify-between",
                )}
              >
                {item.meta_image ? (
                  <Image
                    src={item.meta_image}
                    alt={item.meta_alt_image || item.meta_title || ""}
                    width={500}
                    height={CARD_IMAGE_HEIGHT}
                    loading="lazy"
                    quality={85}
                    className="h-auto w-[70px] shrink-0 object-cover tab:h-48 tab:w-full"
                  />
                ) : null}

                <div className="flex flex-1 flex-col gap-1.5 p-3 tab:p-4">
                  <p className="flex items-center gap-2 text-xs font-semibold tab:hidden">
                    <DynamicIcon
                      iconName="graduation"
                      width={COMPACT_ICON_SIZE}
                      height={COMPACT_ICON_SIZE}
                      color={cta_text_color}
                    />
                    {item.meta_title}
                  </p>

                  <p
                    className="hidden text-xs font-bold tab:block"
                    style={{ color: cta_text_color }}
                  >
                    {item.meta_title}
                  </p>

                  {item.meta_description ? (
                    <p className="text-[11px] text-[#666] tab:text-sm tab:text-[#101112]">
                      {item.meta_description}
                    </p>
                  ) : null}

                  {item.meta_sub_title ? (
                    <p className="text-xs font-bold">
                      Duration:{" "}
                      <span className="font-light">{item.meta_sub_title}</span>
                    </p>
                  ) : null}

                  <div className="mt-auto flex w-full items-center gap-3 pt-2">
                    {secondary}
                    {primary}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Specialisation5017;
